import { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ENTERPRISE_MEMBER_INVITATION_STATUS,
  EnterpriseMember,
} from 'models/Enterprises';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { getArrayLength } from 'utils';
import api from 'api';

export default function useInvitations(
  enterpriseId: string,
  updateMembers: (members: EnterpriseMember[]) => void,
) {
  const { popSuccess, popServerError } = useContext(PopNotificationsContext);

  const { t } = useTranslation();

  const handleUpdate = useCallback(
    (members: EnterpriseMember[], updatedValues: Partial<EnterpriseMember>) =>
      updateMembers(
        members.map((member) => ({
          ...member,
          ...updatedValues,
        })),
      ),
    [updateMembers],
  );

  const onSendInvites = useCallback(
    async (members: EnterpriseMember[]) => {
      handleUpdate(members, {
        lastInviteSent: new Date(),
        invitationStatus: ENTERPRISE_MEMBER_INVITATION_STATUS.INVITATION_SENT,
      });

      try {
        await api.enterprises.sendInvites(
          enterpriseId,
          members.map(({ _id }) => _id),
        );
        popSuccess(
          t('sendInvitationsSuccess', { count: getArrayLength(members) }),
        );
      } catch (e) {
        popServerError(e);
        handleUpdate(members, {
          invitationStatus: ENTERPRISE_MEMBER_INVITATION_STATUS.NOT_INVITED,
        });
      }
    },
    [enterpriseId, handleUpdate, popServerError, popSuccess, t],
  );

  const onSendReminders = useCallback(
    async (members: EnterpriseMember[]) => {
      handleUpdate(members, {
        lastInviteSent: new Date(),
      });

      try {
        await api.enterprises.sendInvites(
          enterpriseId,
          members.map(({ _id }) => _id),
        );
        popSuccess(
          t('sendRemindersSuccess', { count: getArrayLength(members) }),
        );
      } catch (e) {
        popServerError(e);
      }
    },
    [handleUpdate, enterpriseId, popSuccess, t, popServerError],
  );

  return { onSendInvites, onSendReminders };
}
